import apiClient from '@/services/api';

/**
 * The server's side of drawing into a company layer (geonexora-server docs/feature-editing-api.md). Geometries are
 * GeoJSON in WGS 84; properties are sent by the names the map's features carry (the server takes a field's key or
 * its column). Refusals come back as {code, message, ...}: see {@link editError}.
 */

export type Geometry = { type: string; coordinates: unknown };
export type OverlapMode = 'refuse' | 'clip' | 'allow';

export interface OverlapPart {
  id: string;
  area: number;
  geometry: Geometry;
}

export interface CheckResult {
  valid: boolean;
  fixed: boolean;
  reason: string | null;
  dimension: number;
  area?: number;
  length?: number;
  geometry: Geometry;
  overlaps: OverlapPart[];
  overlapArea?: number;
  clipped: Geometry | null;
  clipEmpty: boolean;
  near: { id: string; distance: number }[];
}

export interface SavedFeature {
  type: 'Feature';
  id: string;
  geometry: Geometry;
  properties: Record<string, unknown>;
  edit?: { fixed: boolean; reason: string | null; clipped: boolean; overlaps: string[] };
}

export interface JoinResult {
  gap: number;
  geometry: Geometry;
}

/** What the editing session needs from the server; the browser harness passes the same, against a real server. */
export interface EditApi {
  check(geometry: Geometry, id?: string, tolerance?: number): Promise<CheckResult>;
  create(geometry: Geometry, properties: Record<string, unknown>, overlap: OverlapMode): Promise<SavedFeature>;
  update(id: string, change: { geometry?: Geometry; properties?: Record<string, unknown>; overlap?: OverlapMode }): Promise<SavedFeature>;
  remove(id: string): Promise<void>;
  joinPreview(a: Geometry, b: Geometry, tolerance: number): Promise<JoinResult>;
}

export function editApi(workspace: string, layerKey: string): EditApi {
  const base = `/v2/workspaces/${workspace}/layers/${layerKey}/features`;
  return {
    async check(geometry, id, tolerance) {
      return (await apiClient.post<CheckResult>(`${base}/check`, { geometry, id, tolerance })).data;
    },
    async create(geometry, properties, overlap) {
      return (await apiClient.post<SavedFeature>(base, { geometry, properties, overlap })).data;
    },
    async update(id, change) {
      return (await apiClient.put<SavedFeature>(`${base}/${id}`, change)).data;
    },
    async remove(id) {
      await apiClient.delete(`${base}/${id}`);
    },
    async joinPreview(a, b, tolerance) {
      return (await apiClient.post<JoinResult>(`${base}/merge-lines`, { a: { geometry: a }, b: { geometry: b }, tolerance, dryRun: true })).data;
    },
  };
}

/** A refusal as the screen words it: its code, the server's own message, and what came with it. */
export interface EditError {
  code: string;
  message: string;
  status: number;
  body: Record<string, any>;
}

export function editError(e: any): EditError {
  const body = e?.response?.data ?? {};
  return {
    code: typeof body.code === 'string' ? body.code : 'edit.failed',
    message: body.message ?? body.error ?? e?.message ?? String(e),
    status: e?.response?.status ?? 0,
    body,
  };
}

/** The translation key of each refusal the server gives (literal, so the i18n check sees every one). */
export const ERROR_KEYS: Record<string, string> = {
  'edit.bad_geometry': 'editing.errors.bad_geometry',
  'edit.wrong_geometry': 'editing.errors.wrong_geometry',
  'edit.invalid_geometry': 'editing.errors.invalid_geometry',
  'edit.too_large': 'editing.errors.too_large',
  'edit.overlap': 'editing.errors.overlap',
  'edit.clip_empty': 'editing.errors.clip_empty',
  'edit.lines_not_touching': 'editing.errors.lines_not_touching',
  'edit.not_a_single_line': 'editing.errors.not_a_single_line',
  'edit.split_nothing': 'editing.errors.split_nothing',
  'edit.invalid_attributes': 'editing.errors.invalid_attributes',
  'edit.unknown_field': 'editing.errors.unknown_field',
  'edit.not_found': 'editing.errors.not_found',
  'edit.not_editable': 'editing.errors.not_editable',
  'edit.forbidden': 'editing.errors.forbidden',
  'edit.bad_request': 'editing.errors.bad_request',
  'edit.failed': 'editing.errors.failed',
};
